import { useState, useEffect, useCallback } from "react";
import { UserPlus, Trash2, Loader2, Shield, User, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { listUsers, createUser, deleteUser, resetUserPassword, errMsg } from "@/api";

const ROLE_LABEL = { clinic_admin: "Admin de clínica", therapist: "Terapeuta", super_admin: "Super-admin" };

export const TeamManager = ({ clinicId = null, roles = ["therapist"], title = "Equipo" }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: roles[0] });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { setUsers(await listUsers(clinicId)); }
    catch (e) { toast.error(errMsg(e, "No se pudo cargar el equipo")); }
    finally { setLoading(false); }
  }, [clinicId]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, email: form.email.trim().toLowerCase() };
      if (clinicId) payload.clinic_id = clinicId;
      await createUser(payload);
      toast.success("Usuario creado");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: roles[0] });
      load();
    } catch (err) {
      toast.error(errMsg(err, "Error al crear usuario"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`¿Eliminar a ${u.name}?`)) return;
    try { await deleteUser(u.id); toast.success("Usuario eliminado"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const handleReset = async (u) => {
    const pw = window.prompt(`Nueva contraseña para ${u.name}:`);
    if (pw === null) return;
    if (pw.trim().length < 4) { toast.error("La contraseña debe tener al menos 4 caracteres"); return; }
    try { await resetUserPassword(u.id, pw); toast.success(`Contraseña de ${u.name} actualizada`); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-brand-navy">{title}</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-button" className="rounded-full bg-brand-teal hover:bg-brand-teal/90 text-white h-10"><UserPlus className="w-4 h-4" /> Añadir</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="text-brand-navy">Nuevo usuario</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="u-name" className="text-brand-navy">Nombre</Label>
                <Input id="u-name" data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-11" />
              </div>
              <div>
                <Label htmlFor="u-email" className="text-brand-navy">Email</Label>
                <Input id="u-email" data-testid="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 h-11" autoCapitalize="none" />
              </div>
              <div>
                <Label htmlFor="u-pass" className="text-brand-navy">Contraseña</Label>
                <Input id="u-pass" data-testid="user-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 h-11" />
              </div>
              {roles.length > 1 && (
                <div>
                  <Label className="text-brand-navy">Rol</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role-select" className="mt-1 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <DialogFooter>
                <Button type="submit" data-testid="user-save-button" disabled={saving} className="rounded-full bg-brand-navy hover:bg-brand-navy/90 text-white">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Crear
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-brand-teal mx-auto" /></div>
      ) : users.length === 0 ? (
        <p className="text-slate-500 text-sm py-8 text-center">No hay usuarios todavía.</p>
      ) : (
        <div className="grid grid-cols-1 gap-3" data-testid="users-list">
          {users.map((u) => (
            <div key={u.id} data-testid={`user-row-${u.id}`} className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-brand-navy/10 flex items-center justify-center text-brand-navy">
                  {u.role === "therapist" ? <User className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                </div>
                <div>
                  <p className="font-medium text-brand-navy">{u.name}</p>
                  <p className="text-xs text-slate-500">{u.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-brand-teal/10 text-brand-teal border-0">{ROLE_LABEL[u.role]}</Badge>
                <Button variant="outline" size="sm" title="Restablecer contraseña" data-testid={`reset-user-${u.id}`} onClick={() => handleReset(u)} className="rounded-full border-slate-300 text-brand-navy h-9"><KeyRound className="w-4 h-4" /></Button>
                <Button variant="outline" size="sm" data-testid={`delete-user-${u.id}`} onClick={() => handleDelete(u)} className="rounded-full border-slate-300 text-red-500 h-9"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
